import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Card, H2, Tabs, Text } from '@betterment/samba-react';
import { CardLoader, useFeatureFlag } from '@betterment/shared-components';
import { useRequiredParam } from '@betterment/shared-components/src/hooks/useRequiredParam';

import { AccountsTable } from './AccountsTable';
import { EnvelopesTable } from './EnvelopesTable';
import { HouseholdAccountsDocument } from './graphql/query.householdAccounts.generated';
import { HouseholdAccountYtdReturnsDocument } from './graphql/query.householdAccountYtdReturns.generated';
import { HouseholdEnvelopeYtdReturnsDocument } from './graphql/query.householdEnvelopeYtdReturns.generated';
import { getUniqueEnvelopesWithPurposeFromAccounts } from './utils';

export const Accounts: React.FC = () => {
  const householdId = useRequiredParam('householdId');
  const { data } = useGraphQLQuery(HouseholdAccountsDocument, { variables: { id: householdId } });

  const { enabled: isYtdReturnsEnabled } = useFeatureFlag({
    splitName: 'retail.b4a_ax_q4_2025_ytd_returns_enabled',
    context: 'HouseholdSummary',
  });

  const { enabled: isWeightedReturnsEnabled } = useFeatureFlag({
    splitName: 'retail.b4a_ax_q1_2026_weighted_returns_enabled',
    context: 'HouseholdSummary',
  });

  const shouldFetchAccountYtdReturns = isYtdReturnsEnabled || isWeightedReturnsEnabled;
  const shouldFetchEnvelopeYtdReturns = isYtdReturnsEnabled;

  const { data: ytdData, loading: isYtdLoading } = useGraphQLQuery(HouseholdAccountYtdReturnsDocument, {
    variables: { id: householdId },
    skip: !shouldFetchAccountYtdReturns,
  });

  const { data: envelopeYtdData, loading: isEnvelopeYtdLoading } = useGraphQLQuery(
    HouseholdEnvelopeYtdReturnsDocument,
    {
      variables: { id: householdId },
      skip: !shouldFetchEnvelopeYtdReturns,
    }
  );

  if (!data) return <CardLoader />;

  const { envelopes: envelopesWithPurpose, accountHoldersMap } = getUniqueEnvelopesWithPurposeFromAccounts(
    data.household.accounts
  );
  return (
    <Card css={{ padding: 'space.300' }}>
      <Card.Header css={{ mb: 'space.100' }}>
        <H2 variant="title200">Accounts</H2>
      </Card.Header>
      <Card.Content>
        <Tabs alignment="start" defaultValue="accounts" spacing="default" border="bottom">
          <Tabs.List>
            <Tabs.Trigger value="accounts">
              <Text>All accounts ({data.household.accounts.length})</Text>
            </Tabs.Trigger>
            <Tabs.Trigger value="goals">
              <Text>Goals ({envelopesWithPurpose.length})</Text>
            </Tabs.Trigger>
          </Tabs.List>
          <Tabs.Content value="accounts">
            <AccountsTable
              accounts={data.household.accounts}
              ytdReturnsData={ytdData?.household.accountYtdReturns}
              ytdReturnsLoading={isYtdLoading}
              showYtdReturns={shouldFetchAccountYtdReturns}
              isWeightedReturnsEnabled={isWeightedReturnsEnabled}
            />
          </Tabs.Content>
          <Tabs.Content value="goals">
            <EnvelopesTable
              envelopes={envelopesWithPurpose}
              accountHoldersMap={accountHoldersMap}
              ytdReturnsData={envelopeYtdData?.household.envelopeYtdReturns}
              ytdReturnsLoading={isEnvelopeYtdLoading}
              showYtdReturns={shouldFetchEnvelopeYtdReturns}
            />
          </Tabs.Content>
        </Tabs>
      </Card.Content>
    </Card>
  );
};
